import React, { useCallback, useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import {
  Badge,
  Button,
  Card,
  DropZone,
  Frame,
  Icon,
  Modal,
  Page,
  Spinner,
  Stack,
  TextField,
  Thumbnail,
  Toast,
} from "@shopify/polaris";

import { ThumbsUpMajor } from "@shopify/polaris-icons";
import { CancelSmallMinor } from "@shopify/polaris-icons";

import feedbackApi from "../../services/feedbackApi";
import "./SuggestFeatureContainer.scss";

const normalizeFeature = (feature) => {
  let status = (feature && feature.status && String(feature.status).toLowerCase()) || "pending";
  if (status === "in progress") status = "in_progress";

  const rawImage = feature && feature.image;
  const baseUrl =
    process.env.REACT_APP_FEEDBACK_BASE_URL ||
    "https://www.mlveda.com/ShopifyApps/FeatureSuggestTest";
  const image =
    rawImage && typeof rawImage === "string"
      ? rawImage.startsWith("http")
        ? rawImage
        : `${baseUrl}${rawImage.startsWith("/") ? "" : "/"}${rawImage}`
      : rawImage;

  return {
    id: (feature && (feature._id || feature.id)) || "",
    title: (feature && feature.title) || "",
    description: (feature && feature.description) || "",
    status,
    votes: (feature && (feature.vote || feature.votes)) || 0,
    userId: (feature && feature.userId && (feature.userId._id || feature.userId)) || feature?.userId,
    image,
    hasVoted: Boolean(feature && (feature.userHasVoted || feature.hasVoted)),
    user:
      feature && feature.user
        ? {
          _id: feature.user._id,
          userEmail: feature.user.userEmail,
          userName: feature.user.userName,
        }
        : undefined,
  };
};

const unwrapApiFeature = (payload) => {
  // Support common response shapes:
  // - { data: { ...feature } }
  // - { data: { feature: { ... } } }
  // - { feature: { ... } }
  // - feature
  if (!payload) return null;
  if (payload.feature) return payload.feature;
  if (payload.data && payload.data.feature) return payload.data.feature;
  if (payload.data) return payload.data;
  return payload;
};

const unwrapApiFeaturesList = (payload) => {
  // Support common response shapes:
  // - { data: { features: [...] } }
  // - { data: { data: { features: [...] } } }
  // - { features: [...] }
  if (!payload) return [];
  return (
    payload?.data?.features ||
    payload?.data?.data?.features ||
    payload?.features ||
    []
  );
};

const SuggestFeatureContainer = (props) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuggest, setShowSuggest] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [editingFeature, setEditingFeature] = useState(null);
  const [title, setTitle] = useState("");
  const [desc, setDesc] = useState("");
  const [editTitle, setEditTitle] = useState("");
  const [editDesc, setEditDesc] = useState("");
  const [files, setFiles] = useState([]);
  const [editFiles, setEditFiles] = useState([]);
  const [existingImageUrl, setExistingImageUrl] = useState(null);
  const [toastActive, setToastActive] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [toastError, setToastError] = useState(false);

  const [features, setFeatures] = useState([]);
  const [currentUserId, setCurrentUserId] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [editInitialSnapshot, setEditInitialSnapshot] = useState(null);
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
  const [imagePreviewOpen, setImagePreviewOpen] = useState(false);
  const [previewImageUrl, setPreviewImageUrl] = useState("");

  const storeDetails = (props.auth && props.auth.storeDetails) || {};
  // Feedback API app name (as per your requirement)
  const appName = "MCP";
  const storeName =
    storeDetails.myshopify_domain ||
    "";
  const userName =
    storeDetails.shopOwner ||
    storeDetails.shop_owner ||
    storeDetails.owner ||
    storeDetails.name ||
    "";
  const userEmail =
    storeDetails.shopEmail ||
    storeDetails.shop_email ||
    storeDetails.email ||
    "";

  const stripHtml = useCallback((value = "") => {
    return String(value)
      .replace(/<[^>]*>/g, " ")
      .replace(/&nbsp;/g, " ")
      .replace(/\s+/g, " ")
      .trim();
  }, []);

  const plainDesc = useMemo(() => stripHtml(desc), [desc, stripHtml]);
  const plainEditDesc = useMemo(() => stripHtml(editDesc), [editDesc, stripHtml]);

  const isFormValid = title.trim().length > 0 && plainDesc.length > 0;
  const isEditFormValid =
    editTitle.trim().length > 0 && plainEditDesc.length > 0;

  const toggleToastActive = useCallback(
    () => setToastActive((prev) => !prev),
    []
  );

  const showToast = useCallback((message, isError = false) => {
    setToastMessage(message);
    setToastError(isError);
    setToastActive(true);
  }, []);

  const getErrorMessage = useCallback((error) => {
    return (
      error?.response?.data?.message ||
      error?.response?.data?.error ||
      error?.response?.data?.data?.message ||
      error?.error?.message ||
      error?.message ||
      "Failed. Please try again."
    );
  }, []);

  const MAX_IMAGE_BYTES = 5 * 1024 * 1024; // 5MB

  const handleDropZoneDrop = useCallback((_dropFiles, acceptedFiles) => {
    if (!acceptedFiles || acceptedFiles.length === 0) return;
    const file = acceptedFiles[0];
    if (file?.size && file.size > MAX_IMAGE_BYTES) {
      showToast("Image must be 5MB or smaller.", true);
      return;
    }
    setFiles([file]);
  }, [MAX_IMAGE_BYTES, showToast]);

  const handleEditDropZoneDrop = useCallback((_dropFiles, acceptedFiles) => {
    if (!acceptedFiles || acceptedFiles.length === 0) return;
    const file = acceptedFiles[0];
    if (file?.size && file.size > MAX_IMAGE_BYTES) {
      showToast("Image must be 5MB or smaller.", true);
      return;
    }
    setEditFiles([file]);
    setExistingImageUrl(null);
  }, [MAX_IMAGE_BYTES, showToast]);

  const fileUpload = useMemo(() => {
    if (files.length > 0) return null;
    return (
      <DropZone.FileUpload actionTitle="Add image" actionHint="PNG, JPG up to 5MB" />
    );
  }, [files.length]);

  const editFileUpload = useMemo(() => {
    if (editFiles.length > 0) return null;
    return (
      <DropZone.FileUpload actionTitle="Add image" actionHint="PNG, JPG up to 5MB" />
    );
  }, [editFiles.length]);

  const getPreviewSource = useCallback((file) => {
    if (typeof window === "undefined" || !window.URL || !window.URL.createObjectURL) return "";
    return window.URL.createObjectURL(file);
  }, []);

  const uploadedFiles = useMemo(() => {
    if (!files || files.length === 0) return null;
    return (
      <Stack vertical spacing="tight">
        {files.map((file, index) => (
          <Stack alignment="center" spacing="tight" key={`${file.name}-${index}`}>
            <Thumbnail
              size="small"
              alt={file.name}
              source={getPreviewSource(file)}
            />
            <div style={{ wordBreak: "break-word", flex: 1 }}>{file.name}</div>
            <Button
              plain
              accessibilityLabel="Remove file"
              onClick={() => setFiles([])}
            >
              <Icon source={CancelSmallMinor} />
            </Button>
          </Stack>
        ))}
      </Stack>
    );
  }, [files, getPreviewSource]);

  const uploadedEditFiles = useMemo(() => {
    if (!editFiles || editFiles.length === 0) return null;
    return (
      <Stack vertical spacing="tight">
        {editFiles.map((file, index) => (
          <Stack alignment="center" spacing="tight" key={`${file.name}-${index}`}>
            <Thumbnail
              size="small"
              alt={file.name}
              source={getPreviewSource(file)}
            />
            <div style={{ wordBreak: "break-word", flex: 1 }}>{file.name}</div>
            <Button
              plain
              accessibilityLabel="Remove file"
              onClick={() => setEditFiles([])}
            >
              <Icon source={CancelSmallMinor} />
            </Button>
          </Stack>
        ))}
      </Stack>
    );
  }, [editFiles, getPreviewSource]);

  const isEditDirty = useMemo(() => {
    if (!editingFeature || !editInitialSnapshot) return false;
    if (editTitle.trim() !== editInitialSnapshot.title) return true;
    if (stripHtml(editDesc) !== editInitialSnapshot.plainDesc) return true;
    if ((editFiles && editFiles.length > 0) !== editInitialSnapshot.hasNewFile) return true;
    if (existingImageUrl !== editInitialSnapshot.existingImageUrl) return true;
    return false;
  }, [
    editDesc,
    editFiles,
    editInitialSnapshot,
    editTitle,
    editingFeature,
    existingImageUrl,
    stripHtml,
  ]);

  // Cleanup object URLs created in previews (best-effort).
  useEffect(() => {
    const urls = [];
    (files || []).forEach((f) => urls.push(getPreviewSource(f)));
    (editFiles || []).forEach((f) => urls.push(getPreviewSource(f)));
    return () => {
      urls.forEach((u) => {
        try {
          if (u) URL.revokeObjectURL(u);
        } catch (e) {
          // ignore
        }
      });
    };
  }, [files, editFiles, getPreviewSource]);

  const loadFeatures = useCallback(async () => {
    setIsLoading(true);
    try {
      await feedbackApi.initializeToken(appName, userName, userEmail);

      let userId = null;
      if (userEmail) {
        try {
          const userRes = await feedbackApi.getUserId(userEmail);
          userId = userRes?.data?._id || userRes?._id || null;
        } catch (e) {
          userId = null;
        }
      }
      if (userId) setCurrentUserId(userId);

      const res = await feedbackApi.getFeatures(userId);
      const raw = unwrapApiFeaturesList(res);
      const next = Array.isArray(raw) ? raw.map(normalizeFeature) : [];
      // Avoid rendering "empty cards" if backend returns placeholder items
      setFeatures(next.filter((f) => f && f.id && f.title));
    } catch (e) {
      showToast("Failed to load feature requests.", true);
      setFeatures([]);
    } finally {
      setIsLoading(false);
      setHasLoadedOnce(true);
    }
  }, [appName, showToast, userEmail, userName]);

  useEffect(() => {
    loadFeatures();
  }, [loadFeatures]);

  const resetSuggestForm = useCallback(() => {
    setTitle("");
    setDesc("");
    setFiles([]);
  }, []);

  const submitFeature = useCallback(async () => {
    if (!isFormValid || isSubmitting) return;
    setIsSubmitting(true);
    try {
      if (files[0]?.size && files[0].size > MAX_IMAGE_BYTES) {
        showToast("Image must be 5MB or smaller.", true);
        return;
      }
      const requestData = {
        userEmail: userEmail || "",
        userName: userName || "",
        platform: "Shopify",
        title: title.trim(),
        description: desc,
        appName: appName || "",
        storeName: storeName || "",
      };
      if (files[0]) requestData.image = files[0];

      const featureData = await feedbackApi.createFeatureRequest(requestData);

      if (featureData) {
        const newFeature = normalizeFeature(unwrapApiFeature(featureData));
        if (newFeature && newFeature.id && newFeature.title) {
          setFeatures((prev) => [newFeature, ...prev]);
        } else {
          await loadFeatures();
        }
      } else {
        await loadFeatures();
      }

      showToast("Feature created successfully!");
      setShowSuggest(false);
      resetSuggestForm();
    } catch (e) {
      showToast(getErrorMessage(e), true);
    } finally {
      setIsSubmitting(false);
    }
  }, [
    appName,
    desc,
    files,
    getErrorMessage,
    isFormValid,
    isSubmitting,
    loadFeatures,
    MAX_IMAGE_BYTES,
    resetSuggestForm,
    showToast,
    title,
    userEmail,
    userName,
  ]);

  const submitEditFeature = useCallback(async () => {
    if (!isEditFormValid || isSubmitting || !editingFeature) return;
    setIsSubmitting(true);
    try {
      if (editFiles[0]?.size && editFiles[0].size > MAX_IMAGE_BYTES) {
        showToast("Image must be 5MB or smaller.", true);
        return;
      }
      const updateData = {
        userId: currentUserId,
        title: editTitle.trim(),
        description: editDesc,
        storeName: storeName || "",
      };
      if (editFiles[0]) updateData.image = editFiles[0];
      // If user removed existing image and didn't upload a new one, tell API to remove it.
      const removedExistingImage =
        !!editInitialSnapshot?.existingImageUrl &&
        !existingImageUrl &&
        !(editFiles && editFiles.length > 0);
      if (removedExistingImage) {
        // Some backends expect explicit null for clearing
        updateData.image = "";
      }

      const featureData = await feedbackApi.updateFeature(editingFeature.id, updateData);

      if (featureData) {
        const updated = normalizeFeature(unwrapApiFeature(featureData));
        if (updated && updated.id && updated.title) {
          setFeatures((prev) =>
            prev.map((f) => (f.id === editingFeature.id ? updated : f))
          );
        } else {
          await loadFeatures();
        }
      } else {
        await loadFeatures();
      }

      showToast("Feature updated successfully!");
      setShowEdit(false);
      setEditingFeature(null);
      setExistingImageUrl(null);
      setEditFiles([]);
        setEditInitialSnapshot(null);
    } catch (e) {
      showToast(getErrorMessage(e), true);
    } finally {
      setIsSubmitting(false);
    }
  }, [
    currentUserId,
    editDesc,
    editFiles,
    editTitle,
    editInitialSnapshot,
    editingFeature,
    existingImageUrl,
    getErrorMessage,
    isEditFormValid,
    isSubmitting,
    loadFeatures,
    MAX_IMAGE_BYTES,
    showToast,
  ]);

  const toggleVote = useCallback(
    async (id) => {
      const feature = features.find((item) => item.id === id);
      const isAlreadyUpvoted = feature && feature.hasVoted === true;
      const voteAction = isAlreadyUpvoted ? "remove" : "upvote";

      try {
        const voteData = await feedbackApi.voteOnFeature(id, voteAction, userName, userEmail);

        setFeatures((prev) =>
          prev.map((f) => {
            if (f.id !== id) return f;
            if (voteData && typeof voteData.totalVotes === "number") {
              return { ...f, votes: voteData.totalVotes, hasVoted: !!voteData.userHasVoted };
            }
            const delta = voteAction === "upvote" ? 1 : -1;
            return {
              ...f,
              votes: Math.max(0, (f.votes || 0) + delta),
              hasVoted: voteAction === "upvote",
            };
          })
        );
      } catch (e) {
        showToast("Failed to update vote.", true);
      }
    },
    [features, showToast, userEmail, userName]
  );

  const handleEditFeature = useCallback(
    (feature) => {
      const canEdit =
        currentUserId &&
        feature.userId === currentUserId &&
        feature.status === "pending";
      if (!canEdit) return;

      setEditingFeature(feature);
      setEditTitle(feature.title);
      setEditDesc(feature.description);
      setEditFiles([]);
      setExistingImageUrl(feature.image || null);
      setEditInitialSnapshot({
        title: (feature.title || "").trim(),
        plainDesc: stripHtml(feature.description || ""),
        existingImageUrl: feature.image || null,
        hasNewFile: false,
      });
      setShowEdit(true);
    },
    [currentUserId, stripHtml]
  );

  const sorted = useMemo(() => {
    return [...features].sort((a, b) => (b.votes || 0) - (a.votes || 0));
  }, [features]);

  const pending = sorted.filter((f) => f.status === "pending");
  const approved = sorted.filter((f) => f.status === "approved");
  const inProgress = sorted.filter((f) => f.status === "in_progress");
  const done = sorted.filter((f) => f.status === "done");
  const rejected = sorted.filter((f) => f.status === "rejected");

  const RoadmapCard = ({ feature }) => {
    const canEdit =
      currentUserId &&
      feature.userId === currentUserId &&
      feature.status === "pending";

    return (
      <Card sectioned>
        <Stack vertical spacing="tight">
          <Stack distribution="equalSpacing" alignment="center" wrap={false}>
            <Stack.Item fill>
              <div className="segFeature__title">
                {feature.title}
              </div>
              <div
                className="segFeature__desc"
                dangerouslySetInnerHTML={{ __html: feature.description }}
              />
            </Stack.Item>

            <Stack.Item>
              <Stack spacing="tight" alignment="center" wrap={false}>
                {feature.status !== "done" ? (
                  <>
                    <div className="segFeature__voteGroup">

                      <div
                        className="segFeature__votes"
                      >
                        {feature.votes}
                      </div>
                      <Button
                        size="slim"
                        onClick={() => toggleVote(feature.id)}
                        pressed={!!feature.hasVoted}
                        icon={ThumbsUpMajor}
                        accessibilityLabel={
                          feature.hasVoted ? "Remove vote" : "Upvote"
                        }
                      >
                      </Button>
                    </div>

                  </>
                ) : null}
                {canEdit ? (
                  <Button
                    size="slim"
                    primary
                    onClick={() => handleEditFeature(feature)}
                  >
                    Edit
                  </Button>
                ) : null}
              </Stack>
            </Stack.Item>
          </Stack>


        </Stack>
      </Card>
    );
  };

  const Column = ({ title: colTitle, items }) => (
    <Card sectioned>
      <Stack vertical spacing="tight">
        <Stack distribution="equalSpacing" alignment="center">
          <div className="segFeature__columnTitle">{colTitle}</div>
          <Badge>{String(items.length)}</Badge>
        </Stack>
        <Stack vertical spacing="tight">
          {items.map((f) => (
            <RoadmapCard key={f.id} feature={f} />
          ))}
          {items.length === 0 ? <div className="segFeature__empty">No items</div> : null}
        </Stack>
      </Stack>
    </Card>
  );

  return (
    <Frame>
      <Page
        fullWidth
        title="Roadmap"
        subtitle="Help us improve by sharing your ideas and suggestions"
        primaryAction={{
          content: "Suggest Feature",
          onAction: () => setShowSuggest(true),
        }}
      >
        {!hasLoadedOnce && isLoading ? (
          <div className="segFeature__initialLoader">
            <Spinner accessibilityLabel="Loading roadmap" size="large" />
          </div>
        ) : (

        <div className="segFeature__columnsOuter">
          <div className="segFeature__columnsInner">
            <div className="segFeature__column">
              <Column title="Pending" items={pending} />
            </div>
            <div className="segFeature__column">
              <Column title="Approved" items={approved} />
            </div>
            <div className="segFeature__column">
              <Column title="In Progress" items={inProgress} />
            </div>
            <div className="segFeature__column">
              <Column title="Done" items={done} />
            </div>
            <div className="segFeature__column">
              <Column title="Rejected" items={rejected} />
            </div>
          </div>
        </div>
        )}
      </Page>

      <Modal
        title="Create a feature request"
        open={showSuggest}
        onClose={() => setShowSuggest(false)}
        primaryAction={{
          content: isSubmitting ? "Submitting..." : "Submit",
          onAction: submitFeature,
          disabled: !isFormValid || isSubmitting,
        }}
        secondaryActions={[
          { content: "Cancel", onAction: () => setShowSuggest(false) },
        ]}
      >
        <Modal.Section>
          <Stack vertical spacing="loose">
            <TextField
              label="Feature Title"
              requiredIndicator
              value={title}
              maxLength={100}
              onChange={setTitle}
              autoComplete="off"
            />

            <TextField
              label="Description"
              requiredIndicator
              value={desc}
              onChange={setDesc}
              multiline={6}
              maxLength={1000}
              autoComplete="off"
            />

            <Stack vertical spacing="tight">
              {uploadedFiles && <div>{uploadedFiles}</div>}
              {files.length === 0 ? (
                <DropZone accept="image/*" type="file" onDrop={handleDropZoneDrop}>
                  {fileUpload}
                </DropZone>
              ) : null}
            </Stack>
          </Stack>
        </Modal.Section>
      </Modal>

      <Modal
        title="Edit feature request"
        open={showEdit}
        onClose={() => {
          setShowEdit(false);
          setEditingFeature(null);
          setEditTitle("");
          setEditDesc("");
          setEditFiles([]);
          setExistingImageUrl(null);
          setEditInitialSnapshot(null);
        }}
        primaryAction={{
          content: isSubmitting ? "Updating..." : "Update",
          onAction: submitEditFeature,
          disabled: !isEditFormValid || isSubmitting || !isEditDirty,
        }}
        secondaryActions={[
          {
            content: "Cancel",
            onAction: () => {
              setShowEdit(false);
              setEditingFeature(null);
              setEditTitle("");
              setEditDesc("");
              setEditFiles([]);
              setExistingImageUrl(null);
            },
          },
        ]}
      >
        <Modal.Section>
          <Stack vertical spacing="loose">
            <TextField
              label="Feature Title"
              requiredIndicator
              value={editTitle}
              maxLength={100}
              onChange={setEditTitle}
              autoComplete="off"
            />

            <TextField
              label="Description"
              requiredIndicator
              value={editDesc}
              onChange={setEditDesc}
              multiline={6}
              maxLength={1000}
              autoComplete="off"
            />

            <Stack vertical spacing="tight">
              {existingImageUrl ? (
                <Stack alignment="center" spacing="tight">
                  <div
                    className="segFeature__existingPreviewTrigger"
                    role="button"
                    tabIndex={0}
                    onClick={() => {
                      setPreviewImageUrl(existingImageUrl);
                      setImagePreviewOpen(true);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        setPreviewImageUrl(existingImageUrl);
                        setImagePreviewOpen(true);
                      }
                    }}
                  >
                    <Stack alignment="center" spacing="tight">
                      <Thumbnail size="small" alt="Existing attachment" source={existingImageUrl} />
                      <div className="segFeature__existingAttachment">Existing attachment</div>
                    </Stack>
                  </div>
                  <Button
                    size="slim"
                    plain
                    onClick={() => setExistingImageUrl(null)}
                    accessibilityLabel="Remove existing image"
                  >
                    <Icon source={CancelSmallMinor} />
                  </Button>
                </Stack>
              ) : null}

              {uploadedEditFiles && <div>{uploadedEditFiles}</div>}

              {editFiles.length === 0 && !existingImageUrl ? (
                <DropZone accept="image/*" type="file" onDrop={handleEditDropZoneDrop}>
                  {editFileUpload}
                </DropZone>
              ) : null}
            </Stack>
          </Stack>
        </Modal.Section>
      </Modal>

      <Modal
        open={imagePreviewOpen}
        onClose={() => setImagePreviewOpen(false)}
        title="Image preview"
        primaryAction={{
          content: "Close",
          onAction: () => setImagePreviewOpen(false),
        }}
      >
        <Modal.Section>
          <div className="segFeature__imagePreviewWrap">
            {previewImageUrl ? (
              <img
                src={previewImageUrl}
                alt=""
                className="segFeature__imagePreviewImg"
              />
            ) : null}
          </div>
        </Modal.Section>
      </Modal>

      {toastActive ? (
        <Toast
          content={toastMessage}
          error={toastError}
          onDismiss={toggleToastActive}
        />
      ) : null}
    </Frame>

  );
};

const mapStateToProps = (state) => {
  return {
    auth: state.auth,
  };
};

export default connect(mapStateToProps)(SuggestFeatureContainer);